// Updated BulletinContentService with proper data transformation
import {
  prepareBlockDataForBackend,
  transformBackendDataForFrontend,
} from "../utils/blockDataUtils";

export class BulletinContentService {
  constructor(fetchData) {
    this.fetchData = fetchData;
  }

  // Transform data before sending to backend
  transformBlockForBackend(block) {
    return prepareBlockDataForBackend(block);
  }

  // Transform data after receiving from backend
  transformBlockFromBackend(block) {
    return transformBackendDataForFrontend(block);
  }

  // Get all content for a section with proper transformation
  async getAllContent(bulletinId, sectionId, params = {}) {
    const response = await this.fetchData({
      url: `ebulletin/bulletins/${bulletinId}/sections/${sectionId}/content`,
      method: "GET",
      params,
    });

    // Transform backend data for frontend use
    if (response.data && Array.isArray(response.data)) {
      response.data = response.data.map(this.transformBlockFromBackend);
    }

    return response;
  }

  // Create new content with proper serialization
  async createContent(bulletinId, sectionId, data) {
    const transformedData = this.transformBlockForBackend(data);
    return await this.fetchData({
      url: `ebulletin/bulletins/${bulletinId}/sections/${sectionId}/content`,
      method: "POST",
      data: transformedData,
    });
  }

  // Get specific content with transformation
  async getContent(bulletinId, sectionId, contentId) {
    const response = await this.fetchData({
      url: `ebulletin/bulletins/${bulletinId}/sections/${sectionId}/content/${contentId}`,
      method: "GET",
    });

    // Transform single block
    if (response.data) {
      response.data = this.transformBlockFromBackend(response.data);
    }

    return response;
  }

  // Update content with proper serialization
  async updateContent(bulletinId, sectionId, contentId, data) {
    const transformedData = this.transformBlockForBackend(data);
    return await this.fetchData({
      url: `ebulletin/bulletins/${bulletinId}/sections/${sectionId}/content/${contentId}`,
      method: "PUT",
      data: transformedData,
    });
  }

  // Delete content
  async deleteContent(bulletinId, sectionId, contentId) {
    const adminId = 1;
    return await this.fetchData({
      url: `ebulletin/bulletins/${bulletinId}/sections/${sectionId}/content/${contentId}`,
      method: "DELETE",
      data: { admin_id: adminId },
    });
  }

  // Upload file and get URL - specifically for your use case
  async uploadFile(file, type = "general") {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type); // 'image', 'video', 'file'

    try {
      const response = await this.fetchData({
        url: `ebulletin/upload`,
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Return the URL in the format your frontend expects
      return {
        data: {
          url: response.data?.url || response.data?.data?.url,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          ...response.data,
        },
      };
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  }

  // Create multiple content blocks with proper transformation
  async createBulk(bulletinId, sectionId, payload) {
    try {
      // Transform all blocks in the payload
      if (payload.blocks && Array.isArray(payload.blocks)) {
        payload.blocks = payload.blocks.map(this.transformBlockForBackend);
      }

      return await this.fetchData({
        url: `ebulletin/bulletins/${bulletinId}/sections/${sectionId}/content/bulk`,
        method: "POST",
        data: payload,
      });
    } catch (error) {
      console.error("createBulk error:", error.response?.data || error.message);
      throw error;
    }
  }

  // Update multiple content blocks with proper transformation
  async updateBulk(bulletinId, sectionId, payload) {
    try {
      // Add validation before sending
      if (
        !payload ||
        (!Array.isArray(payload) && typeof payload !== "object")
      ) {
        throw new Error("Invalid payload format for bulk update");
      }

      // Transform blocks data
      if (payload.blocks && Array.isArray(payload.blocks)) {
        payload.blocks = payload.blocks.map(this.transformBlockForBackend);
      } else if (Array.isArray(payload)) {
        payload = payload.map(this.transformBlockForBackend);
      }

      const response = await this.fetchData({
        url: `ebulletin/bulletins/${bulletinId}/sections/${sectionId}/content/bulk`,
        method: "PUT",
        data: payload,
      });

      // Transform response data back for frontend
      if (response.data && Array.isArray(response.data)) {
        response.data = response.data.map(this.transformBlockFromBackend);
      }

      return response;
    } catch (error) {
      console.error("updateBulk error:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        payload: payload,
      });
      throw error;
    }
  }

  // Reorder content blocks
  async reorderContent(bulletinId, sectionId, payload) {
    try {
      return await this.fetchData({
        url: `ebulletin/bulletins/${bulletinId}/sections/${sectionId}/content/reorder`,
        method: "POST",
        data: payload,
      });
    } catch (error) {
      console.error(
        "reorderContent error:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  // Utility method to validate payload structure
  validateBulkPayload(payload) {
    if (!payload) {
      throw new Error("Payload is required");
    }

    if (Array.isArray(payload)) {
      // Validate each item in array
      payload.forEach((item, index) => {
        if (!item.id && !item.type) {
          throw new Error(`Item at index ${index} missing required fields`);
        }
      });
    } else if (typeof payload === "object") {
      // Validate object structure
      if (!payload.items && !payload.content && !payload.blocks) {
        throw new Error(
          "Payload must contain items, content, or blocks property"
        );
      }
    } else {
      throw new Error("Payload must be an array or object");
    }

    return true;
  }

  // Helper method to handle two-column blocks
  transformTwoColumnBlock(block) {
    if (block.layout === "two-column") {
      return {
        ...block,
        leftBlock: block.leftBlock
          ? this.transformBlockForBackend(block.leftBlock)
          : null,
        rightBlock: block.rightBlock
          ? this.transformBlockForBackend(block.rightBlock)
          : null,
      };
    }
    return this.transformBlockForBackend(block);
  }
}
